import { useMemo } from 'react'
import { ErrorBox } from '@/components/commons/error-box'
import { Flexbox } from '@/components/ui/flexbox'
import { AlertTriangleIcon } from '@/components/ui/icons'
import { useGetGalleryImages } from '@/features/service-vetting/query/use-get-gallery-images'
import { useTranslation } from '@/translations/i18n'
import { ServiceVettingCriteriaGallery } from './service-vetting-criteria-gallery'
import { ServiceVettingCriteriaGallerySkeleton } from './service-vetting-criteria-gallery-skeleton'
import { ServiceVettingCriteriaRejectedImages } from './service-vetting-criteria-rejected-images'

interface ServiceVettingCriteriaGalleryDataInitializerProps {
	serviceId: number
	galleryId: number
	hideAction: boolean
	isExpertiseDeclined: boolean
	isProPhotographyRequired: boolean
	lackPhotoVariety: boolean
}

export const ServiceVettingCriteriaGalleryContainer = ({
	serviceId,
	galleryId,
	hideAction,
	isExpertiseDeclined,
	isProPhotographyRequired,
	lackPhotoVariety
}: ServiceVettingCriteriaGalleryDataInitializerProps) => {
	const { t } = useTranslation()

	// Fetch the gallery images
	const { data, isLoading, isError } = useGetGalleryImages({
		serviceId,
		galleryId
	})

	// Handle gallery images data
	const galleryImages = useMemo(() => {
		return data || []
	}, [data])

	const rejectedImages = useMemo(() => {
		return (
			galleryImages.filter((image) => image.is_approved === false) || []
		)
	}, [galleryImages])

	if (isLoading) {
		return <ServiceVettingCriteriaGallerySkeleton />
	}

	if (isError) {
		return (
			<ErrorBox
				icon={<AlertTriangleIcon />}
				title={t('errors:default')}
			/>
		)
	}

	return (
		<Flexbox direction='column' fullWidth gap={6}>
			<ServiceVettingCriteriaGallery
				galleryId={galleryId}
				galleryImages={galleryImages}
				hideAction={hideAction}
				isExpertiseDeclined={isExpertiseDeclined}
				isProPhotographyRequired={isProPhotographyRequired}
				lackPhotoVariety={lackPhotoVariety}
				serviceId={serviceId}
			/>
			{rejectedImages.length > 0 && (
				<ServiceVettingCriteriaRejectedImages
					galleryId={galleryId}
					hideAction={hideAction}
					isExpertiseDeclined={isExpertiseDeclined}
					rejectedImages={rejectedImages}
				/>
			)}
		</Flexbox>
	)
}
